import './patch-pointer-lock';
import { WebPCodec } from '@playcanvas/splat-transform';
import { Color, createGraphicsDevice, Vec3 } from 'playcanvas';

import { registerCameraPosesEvents } from './camera-poses';
import { registerDocEvents } from './doc';
import { EditHistory } from './edit-history';
import { registerEditorEvents } from './editor';
import { Events } from './events';
import { initFileHandler } from './file-handler';
import { registerIframeApi } from './iframe-api';
import { registerPlySequenceEvents } from './ply-sequence';
import { registerPublishEvents } from './publish';
import { registerRenderEvents } from './render';
import { Scene } from './scene';
import { getSceneConfig } from './scene-config';
import { registerSelectionEvents } from './selection';
import { ShortcutManager } from './shortcut-manager';
import { registerTimelineEvents } from './timeline';
import { BoxSelection } from './tools/box-selection';
import { BrushSelection } from './tools/brush-selection';
import { CoordTool } from './tools/coord-tool';
import { EyedropperSelection } from './tools/eyedropper-selection';
import { FloodSelection } from './tools/flood-selection';
import { LassoSelection } from './tools/lasso-selection';
import { MapPlotWorkflow } from './tools/map-plot-tool';
import { MeasureTool } from './tools/measure-tool';
import { MoveTool } from './tools/move-tool';
import { PolygonSelection } from './tools/polygon-selection';
import { RectSelection } from './tools/rect-selection';
import { RfiTool } from './tools/rfi-tool';
import { RotateTool } from './tools/rotate-tool';
import { ScaleTool } from './tools/scale-tool';
import { SphereSelection } from './tools/sphere-selection';
import { BoundaryPolygonTool } from './tools/boundary-polygon-tool';
import { OrbitAnchorTool } from './tools/orbit-anchor-tool';
import { ToolManager } from './tools/tool-manager';
import { registerTrackManagerEvents } from './track-manager';
import { registerTransformHandlerEvents } from './transform-handler';
import { EditorUI } from './ui/editor';
import { localizeInit } from './ui/localization';
import { registerVolugraphLightboxPlane } from './volugraph-lightbox-plane';
import { registerVolugraphCapturePin } from './volugraph-capture-pin';
import { registerVolugraphReveal } from './volugraph-reveal';
import { registerVolugraphSemantic } from './volugraph-semantic';
import { computeDensityFocus } from './volugraph-auto-center';
import { collectApproximateSplatCentersWorld, writeLas12Format2 } from './volugraph-las-writer';
import { ElementType } from './element';
import { Splat } from './splat';
import { setVolugraphSplatMrtEnabled, shouldUseSplatMrt } from './volugraph-splat-target-format';
declare global {
    interface LaunchParams {
        readonly files: FileSystemFileHandle[];
    }

    interface Window {
        launchQueue: {
            setConsumer: (callback: (launchParams: LaunchParams) => void) => void;
        };
        scene: Scene;
    }
}

const getURLArgs = () => {
    // extract settings from command line in non-prod builds only
    const config = {};

    const apply = (key: string, value: string) => {
        let obj: any = config;
        key.split('.').forEach((k, i, a) => {
            if (i === a.length - 1) {
                obj[k] = value;
            } else {
                if (!obj.hasOwnProperty(k)) {
                    obj[k] = {};
                }
                obj = obj[k];
            }
        });
    };

    const params = new URLSearchParams(window.location.search.slice(1));
    params.forEach((value: string, key: string) => {
        apply(key, value);
    });

    return config;
};

/**
 * Volugraph embed sets __VOLUGRAPH_SUPER_SPLAT_ROOT__ from React before loading this module.
 * React Strict Mode can clear that global between unmount/remount while the ES module is still
 * loading — EditorUI would then fall back to document.body and append a full second viewer,
 * blowing up page height and scroll. Wait briefly for the root before building UI.
 */
const waitForVolugraphEmbedMount = async (): Promise<boolean> => {
    const w = window as unknown as {
        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbed?: boolean };
        __VOLUGRAPH_SUPER_SPLAT_ROOT__?: HTMLElement;
        __VOLUGRAPH_SUPER_SPLAT_LOAD__?: unknown;
    };
    // Embed: React sets LOAD + config + root before appending this script. If main() runs before
    // config is assigned, LOAD still identifies a Volugraph load — never fall back to document.body.
    const isVolugraphEmbed =
        w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphEmbed === true || !!w.__VOLUGRAPH_SUPER_SPLAT_LOAD__;
    if (!isVolugraphEmbed) {
        return true;
    }
    if (w.__VOLUGRAPH_SUPER_SPLAT_ROOT__) {
        return true;
    }
    for (let i = 0; i < 80; i++) {
        if (w.__VOLUGRAPH_SUPER_SPLAT_ROOT__) {
            return true;
        }
        await new Promise((resolve) => setTimeout(resolve, 10));
    }
    // eslint-disable-next-line no-console
    console.warn('[Volugraph] SuperSplat embed: mount root missing — aborting (prevents ghost viewer on document.body)');
    return false;
};

type VolugraphSession = {
    scene: Scene;
    events: Events;
    editorUI: EditorUI;
};

let volugraphActiveSession: VolugraphSession | null = null;
let volugraphLocalizationPromise: Promise<void> | null = null;
/** Bumped on destroy — pending mount timers/rAF bail when stale. */
let volugraphMountGeneration = 0;
let volugraphTransformTimer: number | null = null;
let volugraphFrameCameraRaf: number | null = null;

function cancelVolugraphPendingMountWork(): void {
    volugraphMountGeneration += 1;
    if (volugraphTransformTimer != null) {
        window.clearTimeout(volugraphTransformTimer);
        volugraphTransformTimer = null;
    }
    if (volugraphFrameCameraRaf != null) {
        window.cancelAnimationFrame(volugraphFrameCameraRaf);
        volugraphFrameCameraRaf = null;
    }
}

function ensureLocalization(): Promise<void> {
    if (!volugraphLocalizationPromise) {
        volugraphLocalizationPromise = localizeInit().then(() => {});
    }
    return volugraphLocalizationPromise;
}

/**
 * Teardown embed so volugraphMount() can run again without reloading the ES module (stable index.js URL).
 */
async function volugraphDestroy(): Promise<void> {
    cancelVolugraphPendingMountWork();
    const session = volugraphActiveSession;
    if (session) {
        volugraphActiveSession = null;
        const app = session.scene?.app;
        const device = app?.graphicsDevice as unknown as {
            gl?: WebGLRenderingContext | WebGL2RenderingContext;
            canvas?: HTMLCanvasElement;
        };
        const gl = device?.gl;
        const canvas = device?.canvas;
        try {
            session.scene.clear();
        } catch {
            /* ignore */
        }
        try {
            app?.destroy();
        } catch {
            /* ignore */
        }
        try {
            (window as unknown as { __VOLUGRAPH_TEARDOWN__?: boolean }).__VOLUGRAPH_TEARDOWN__ = true;
            gl?.getExtension('WEBGL_lose_context')?.loseContext();
        } catch {
            /* ignore */
        } finally {
            try {
                delete (window as unknown as { __VOLUGRAPH_TEARDOWN__?: boolean }).__VOLUGRAPH_TEARDOWN__;
            } catch {
                /* ignore */
            }
        }
        try {
            if (canvas) {
                canvas.width = 0;
                canvas.height = 0;
            }
        } catch {
            /* ignore */
        }
        try {
            delete window.scene;
        } catch {
            /* ignore */
        }
        try {
            delete (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_EVENTS__?: Events }).__VOLUGRAPH_SUPER_SPLAT_EVENTS__;
        } catch {
            /* ignore */
        }
        try {
            delete (window as unknown as { __VOLUGRAPH_SPLAT_LOAD_COMPLETE__?: boolean }).__VOLUGRAPH_SPLAT_LOAD_COMPLETE__;
        } catch {
            /* ignore */
        }
    }
    const root = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_ROOT__?: HTMLElement }).__VOLUGRAPH_SUPER_SPLAT_ROOT__;
    if (root) {
        try {
            root.innerHTML = '';
        } catch {
            /* ignore */
        }
    }
    /* bgClr falls back to document.body when root is missing — that inline style survives SPA navigations (e.g. Studio → Landing). */
    try {
        const cfg = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbed?: boolean } }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
        if (cfg?.volugraphEmbed) {
            document.body.style.removeProperty('background-color');
        }
    } catch {
        /* ignore */
    }
    volugraphLocalizationPromise = null;
}

async function volugraphMount(): Promise<void> {
    await volugraphDestroy();

    // root events object
    const events = new Events();
    (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_EVENTS__?: Events }).__VOLUGRAPH_SUPER_SPLAT_EVENTS__ = events;

    // url
    const url = new URL(window.location.href);

    // edit history
    const editHistory = new EditHistory(events);

    // EditorUI ctor calls events.invoke('scene.dirty') before registerEditorEvents runs — stub now, real impl later.
    // Default clean: button shows EXIT until the real handler (editHistory cursor check) confirms actual edits.
    events.function('scene.dirty', () => false);
    // RfiTool replaces these — stub so early invokes are safe.
    events.function('annotation.dirty', () => false);
    events.function('annotation.markClean', () => {});
    events.function('annotation.hasSelection', () => false);
    events.function('annotation.getSelected', () => null);
    /** CoordTool replaces — stub so early invokes are safe. */
    events.function('coord.export', () => []);
    events.function('coord.entryMode', () => 'modal');
    events.function('coord.extraFieldsForNewMarker', () => ({}));
    events.function('volugraph.coord.hasGoodGisAlignment', () => false);
    events.function('volugraph.coord.projectScenePointToLatLng', () => null);
    events.function('volugraph.measureCalibrationEffective', () => 1);
    events.function('boundary.hasAny', () => false);
    events.function('boundary.export', () => []);
    events.function('boundary.list', () => []);
    events.function('volugraph.worldToScreenBatch', () => []);

    // init localization (once per page — module cache keeps this fast on remount)
    await ensureLocalization();

    if (!(await waitForVolugraphEmbedMount())) {
        try {
            window.dispatchEvent(new CustomEvent('volugraph-splat-load-error', {
                detail: { message: 'SuperSplat embed mount root missing' },
            }));
        } catch {
            /* ignore */
        }
        return;
    }

    // Configure WebP WASM for SOG format (used for both reading and writing)
    const staticBase = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_BASE__?: string }).__VOLUGRAPH_SUPER_SPLAT_BASE__
        || document.baseURI;
    WebPCodec.wasmUrl = new URL('static/lib/webp/webp.wasm', staticBase).toString();

    // register events that only need the events object (before UI is created)
    registerTimelineEvents(events);
    registerCameraPosesEvents(events);
    registerTrackManagerEvents(events);
    registerTransformHandlerEvents(events);
    registerPlySequenceEvents(events);
    registerPublishEvents(events);
    registerIframeApi(events);

    // initialize shortcuts
    const shortcutManager = new ShortcutManager(events);
    events.function('shortcutManager', () => shortcutManager);

    // editor ui
    const editorUI = new EditorUI(events);

    const volugraphConfig = (window as unknown as {
        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbed?: boolean; volugraphHeroEmbed?: boolean; controls?: Record<string, number> };
    }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
    const volugraphEmbed = !!volugraphConfig?.volugraphEmbed;
    const volugraphHeroEmbed = !!volugraphConfig?.volugraphHeroEmbed;

    // create the graphics device
    // preserveDrawingBuffer: required for canvas.toBlob() / thumbnails — otherwise the default buffer is cleared and JPEG is black
    // alpha: landing hero needs transparent clear so the page pointcloud shows through
    // Chrome: preserveDrawingBuffer + alpha often composites the canvas as fully opaque; hero skips preserveDrawingBuffer (no thumbnail in hero).
    const graphicsDevice = await createGraphicsDevice(editorUI.canvas, {
        deviceTypes: ['webgl2'],
        antialias: false,
        depth: false,
        stencil: false,
        xrCompatible: false,
        powerPreference: 'high-performance',
        ...(volugraphHeroEmbed ? { alpha: true, premultipliedAlpha: true } : {}),
        ...(volugraphEmbed && !volugraphHeroEmbed ? { preserveDrawingBuffer: true } : {})
    });
    setVolugraphSplatMrtEnabled(shouldUseSplatMrt(graphicsDevice));
    const overrides = [
        volugraphConfig ?? {},
        getURLArgs()
    ];

    // resolve scene config
    const sceneConfig = getSceneConfig(overrides);

    // construct the manager
    const scene = new Scene(
        events,
        sceneConfig,
        editorUI.canvas,
        graphicsDevice
    );

    // colors
    const bgClr = new Color();
    const selectedClr = new Color();
    const unselectedClr = new Color();
    const lockedClr = new Color();

    const setClr = (target: Color, value: Color, event: string) => {
        if (!target.equals(value)) {
            target.copy(value);
            events.fire(event, target);
        }
    };

    const setBgClr = (clr: Color) => {
        setClr(bgClr, clr, 'bgClr');
    };
    const setSelectedClr = (clr: Color) => {
        setClr(selectedClr, clr, 'selectedClr');
    };
    const setUnselectedClr = (clr: Color) => {
        setClr(unselectedClr, clr, 'unselectedClr');
    };
    const setLockedClr = (clr: Color) => {
        setClr(lockedClr, clr, 'lockedClr');
    };

    events.on('setBgClr', (clr: Color) => {
        setBgClr(clr);
    });
    events.on('setSelectedClr', (clr: Color) => {
        setSelectedClr(clr);
    });
    events.on('setUnselectedClr', (clr: Color) => {
        setUnselectedClr(clr);
    });
    events.on('setLockedClr', (clr: Color) => {
        setLockedClr(clr);
    });

    events.function('bgClr', () => {
        return bgClr;
    });
    events.function('selectedClr', () => {
        return selectedClr;
    });
    events.function('unselectedClr', () => {
        return unselectedClr;
    });
    events.function('lockedClr', () => {
        return lockedClr;
    });

    events.on('bgClr', (clr: Color) => {
        const cnv = (v: number) => `${Math.max(0, Math.min(255, (v * 255))).toFixed(0)}`;
        const a = typeof clr.a === 'number' ? clr.a : 1;
        const rgba = `rgba(${cnv(clr.r)},${cnv(clr.g)},${cnv(clr.b)},${a})`;
        const volugraphRoot = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_ROOT__?: HTMLElement }).__VOLUGRAPH_SUPER_SPLAT_ROOT__;
        const w = window as unknown as {
            __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbed?: boolean };
            __VOLUGRAPH_SUPER_SPLAT_LOAD__?: unknown;
        };
        const isVolugraphEmbed =
            w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphEmbed === true || !!w.__VOLUGRAPH_SUPER_SPLAT_LOAD__;
        if (volugraphRoot) {
            volugraphRoot.style.backgroundColor = rgba;
        } else if (!isVolugraphEmbed) {
            document.body.style.backgroundColor = rgba;
        }
    });
    events.on('selectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('unselectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('lockedClr', (clr: Color) => {
        scene.forceRender = true;
    });

    // initialize colors from application config
    const toColor = (value: { r: number, g: number, b: number, a: number }) => {
        return new Color(value.r, value.g, value.b, value.a);
    };
    setBgClr(toColor(sceneConfig.bgClr));
    // Hero: always force transparent clear (Chrome + embed config edge cases)
    if (volugraphHeroEmbed) {
        setBgClr(new Color(0, 0, 0, 0));
    }
    setSelectedClr(toColor(sceneConfig.selectedClr));
    setUnselectedClr(toColor(sceneConfig.unselectedClr));
    setLockedClr(toColor(sceneConfig.lockedClr));

    // create the mask selection canvas
    const maskCanvas = document.createElement('canvas');
    const maskContext = maskCanvas.getContext('2d');
    maskCanvas.setAttribute('id', 'mask-canvas');
    maskContext.globalCompositeOperation = 'copy';

    const mask = {
        canvas: maskCanvas,
        context: maskContext
    };

    // tool manager
    const toolManager = new ToolManager(events);
    toolManager.register('rectSelection', new RectSelection(events, editorUI.toolsContainer.dom));
    toolManager.register('brushSelection', new BrushSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('floodSelection', new FloodSelection(events, editorUI.toolsContainer.dom, mask, editorUI.canvasContainer));
    toolManager.register('polygonSelection', new PolygonSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('lassoSelection', new LassoSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('sphereSelection', new SphereSelection(events, scene, editorUI.canvasContainer));
    toolManager.register('boxSelection', new BoxSelection(events, scene, editorUI.canvasContainer));
    toolManager.register('eyedropperSelection', new EyedropperSelection(events, editorUI.toolsContainer.dom, editorUI.canvasContainer));
    toolManager.register('move', new MoveTool(events, scene));
    toolManager.register('rotate', new RotateTool(events, scene));
    toolManager.register('scale', new ScaleTool(events, scene));
    toolManager.register('measure', new MeasureTool(events, scene, editorUI.toolsContainer.dom, editorUI.canvasContainer));
    toolManager.register('rfi', new RfiTool(events, scene, editorUI.canvasContainer));
    toolManager.register('coord', new CoordTool(events, scene, editorUI.canvasContainer));
    toolManager.register('boundaryPolygon', new BoundaryPolygonTool(events, scene, editorUI.toolsContainer.dom, editorUI.canvasContainer));
    toolManager.register('orbitAnchor', new OrbitAnchorTool(events, scene, editorUI.canvasContainer));
    new MapPlotWorkflow(events, scene, editorUI.canvasContainer);

    editorUI.toolsContainer.dom.appendChild(maskCanvas);

    window.scene = scene;
    registerVolugraphLightboxPlane(events);
    registerVolugraphCapturePin(events);
    registerVolugraphReveal(events, scene);
    registerVolugraphSemantic(events, scene);
    volugraphActiveSession = { scene, events, editorUI };

    events.on('volugraph.downloadLasApproximate', () => {
        const pts = collectApproximateSplatCentersWorld(scene);
        if (!pts.length) return;
        const buf = writeLas12Format2(pts);
        const blob = new Blob([buf], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const loadCfg = (window as unknown as {
            __VOLUGRAPH_SUPER_SPLAT_LOAD__?: { filename?: string };
        }).__VOLUGRAPH_SUPER_SPLAT_LOAD__;
        const rawName = loadCfg?.filename ?? '';
        const name = rawName ? rawName.replace(/\.[^.]+$/, '') : 'volugraph-capture';
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}-approximate.las`;
        a.click();
        URL.revokeObjectURL(url);
    });

    const wsTmp = new Vec3();
    const wsScr = new Vec3();
    events.removeFunction('volugraph.worldToScreenBatch');
    events.function('volugraph.worldToScreenBatch', (verts: unknown) => {
        if (!Array.isArray(verts)) return [];
        const cam = scene.camera?.camera;
        const cw = scene.canvas?.clientWidth ?? 1;
        const ch = scene.canvas?.clientHeight ?? 1;
        if (!cam) return [];
        return verts.map((row: unknown) => {
            if (!Array.isArray(row) || row.length < 3) return { x: 0, y: 0, ok: false };
            wsTmp.set(Number(row[0]) || 0, Number(row[1]) || 0, Number(row[2]) || 0);
            cam.worldToScreen(wsTmp, wsScr);
            return {
                x: wsScr.x * cw,
                y: wsScr.y * ch,
                ok: Number.isFinite(wsScr.x) && Number.isFinite(wsScr.y)
            };
        });
    });

    /** Embed teardown destroys the app; rAF / React can still fire with a stale `scene` closure → mainCamera.camera undefined */
    const isVolugraphSceneCameraLive = (): boolean => {
        const w = window as unknown as { scene?: Scene };
        if (w.scene !== scene) return false;
        const entity = scene.camera?.mainCamera;
        return !!(entity && entity.camera);
    };

    type VolugraphCameraView = { position: number[]; target: number[] };
    type VolugraphEntityTransform = { position: number[]; quaternion: number[]; scale: number[] };
    type VolugraphOrbitAnchor = { x: number; y: number; z: number };
    type VolugraphSuperSplatConfig = {
        volugraphEditMode?: boolean;
        volugraphInitialCameraView?: VolugraphCameraView;
        volugraphHomeCameraView?: VolugraphCameraView;
        volugraphInitialTransform?: VolugraphEntityTransform;
        volugraphOrbitAnchor?: VolugraphOrbitAnchor;
        volugraphOrbitAnchorZoomOut?: number;
        volugraphAutoCenterDensity?: {
            enabled?: boolean;
            voxelResolution?: number;
            paddingFactor?: number;
            maxSamples?: number;
            liftTowardBoundCenter?: number;
            zoomOutFactor?: number;
        };
    };

    const COMMERCE_ORBIT_ANCHOR_ZOOM_OUT = 1.35;

    const getVolugraphConfig = (): VolugraphSuperSplatConfig | undefined => (
        (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: VolugraphSuperSplatConfig })
            .__VOLUGRAPH_SUPER_SPLAT_CONFIG__
    );

    const snapshotHomeCameraView = (): void => {
        if (!isVolugraphSceneCameraLive()) return;
        const pos = scene.camera.mainCamera.getPosition();
        const fp = scene.camera.focalPointTween.value;
        const cfg = getVolugraphConfig();
        if (!cfg) return;
        cfg.volugraphHomeCameraView = {
            position: [pos.x, pos.y, pos.z],
            target: [fp.x, fp.y, fp.z],
        };
    };

    const applySavedView = (
        view: VolugraphCameraView | null | undefined,
        speed = 0
    ): boolean => {
        if (!isVolugraphSceneCameraLive()) return false;
        if (!view?.position || !view?.target || view.position.length !== 3 || view.target.length !== 3) {
            return false;
        }
        scene.camera.setPose(
            new Vec3(view.position[0], view.position[1], view.position[2]),
            new Vec3(view.target[0], view.target[1], view.target[2]),
            speed
        );
        scene.forceRender = true;
        return true;
    };

    const applyInitialTransform = (transform: VolugraphEntityTransform | null | undefined): boolean => {
        if (!transform) return false;
        const p = transform.position;
        const q = transform.quaternion;
        const s = transform.scale;
        if (!Array.isArray(p) || p.length !== 3) return false;
        if (!Array.isArray(q) || q.length !== 4) return false;
        if (!Array.isArray(s) || s.length !== 3) return false;
        const allFinite = [...p, ...q, ...s].every((n) => typeof n === 'number' && Number.isFinite(n));
        if (!allFinite) return false;
        const sel = events.invoke('selection') as { entity?: {
            setLocalPosition?: (x: number, y: number, z: number) => void;
            setLocalRotation?: (x: number, y: number, z: number, w: number) => void;
            setLocalScale?: (x: number, y: number, z: number) => void;
        } } | Splat | null;
        let entity: {
            setLocalPosition?: (x: number, y: number, z: number) => void;
            setLocalRotation?: (x: number, y: number, z: number, w: number) => void;
            setLocalScale?: (x: number, y: number, z: number) => void;
        } | null = (sel as { entity?: typeof entity })?.entity ?? (sel as Splat)?.entity ?? null;
        if (!entity?.setLocalPosition && scene?.getElementsByType) {
            const splats = scene.getElementsByType(ElementType.splat) as Splat[];
            if (splats.length > 0 && splats[0]?.entity) {
                entity = splats[0].entity;
            }
        }
        if (!entity?.setLocalPosition || !entity?.setLocalRotation || !entity?.setLocalScale) {
            return false;
        }
        try {
            entity.setLocalPosition(p[0], p[1], p[2]);
            entity.setLocalRotation(q[0], q[1], q[2], q[3]);
            entity.setLocalScale(s[0], s[1], s[2]);
            scene.forceRender = true;
            return true;
        } catch {
            return false;
        }
    };

    const scheduleInitialTransformApply = (transform: VolugraphEntityTransform | null | undefined) => {
        if (!transform) return;
        const mountGen = volugraphMountGeneration;
        let attempts = 0;
        const maxAttempts = 450;
        const tick = () => {
            if (mountGen !== volugraphMountGeneration) return;
            if (applyInitialTransform(transform)) return;
            attempts += 1;
            if (attempts < maxAttempts) {
                volugraphTransformTimer = window.setTimeout(tick, 100);
            }
        };
        tick();
    };

    const frameCameraToOrbitAnchor = (speed = 0): boolean => {
        if (!isVolugraphSceneCameraLive()) return false;
        const cfg = getVolugraphConfig();
        const anchor = cfg?.volugraphOrbitAnchor;
        if (
            !anchor
            || typeof anchor.x !== 'number'
            || typeof anchor.y !== 'number'
            || typeof anchor.z !== 'number'
        ) {
            return false;
        }
        const focalPoint = new Vec3(anchor.x, anchor.y, anchor.z);
        const controls = scene.config.controls;

        let radius: number | null = null;
        const autoCfg = cfg?.volugraphAutoCenterDensity;
        if (autoCfg?.enabled) {
            for (const element of scene.elements) {
                if (element.type === ElementType.splat) {
                    const df = computeDensityFocus(element as Splat, autoCfg);
                    if (df) radius = df.radius;
                    break;
                }
            }
        }
        if (radius == null) {
            const zoomOut = cfg?.volugraphOrbitAnchorZoomOut ?? COMMERCE_ORBIT_ANCHOR_ZOOM_OUT;
            radius = Math.max(scene.bound.halfExtents.length() * zoomOut, 0.05);
        }

        scene.camera.setAzimElev(controls.initialAzim, controls.initialElev, 0);
        scene.camera.focus({ focalPoint, radius, speed });
        scene.forceRender = true;
        return true;
    };

    const frameCameraToPipelineDefault = (speed = 0): void => {
        if (!isVolugraphSceneCameraLive()) return;
        const cfg = getVolugraphConfig();
        const autoCfg = cfg?.volugraphAutoCenterDensity;
        if (autoCfg?.enabled) {
            let splat: Splat | null = null;
            for (const element of scene.elements) {
                if (element.type === ElementType.splat) {
                    splat = element as Splat;
                    break;
                }
            }
            const densityFocus = splat ? computeDensityFocus(splat, autoCfg) : null;
            if (densityFocus) {
                const controls = scene.config.controls;
                const azim = densityFocus.azimDeg ?? controls.initialAzim;
                const elev = densityFocus.elevDeg ?? controls.initialElev;
                scene.camera.setAzimElev(azim, elev, 0);
                scene.camera.focus({
                    focalPoint: densityFocus.focalPoint,
                    radius: densityFocus.radius,
                    speed
                });
                scene.forceRender = true;
                return;
            }
        }
        scene.camera.focus();
        scene.forceRender = true;
    };

    const restoreHomeCameraView = (): void => {
        if (!isVolugraphSceneCameraLive()) return;
        const cfg = getVolugraphConfig();
        const saved = cfg?.volugraphInitialCameraView ?? cfg?.volugraphHomeCameraView;
        if (applySavedView(saved, 1.2)) return;
        if (frameCameraToOrbitAnchor(1.2)) return;
        frameCameraToPipelineDefault(1.2);
        snapshotHomeCameraView();
    };

    events.on('volugraph.applySavedCameraView', (view: VolugraphCameraView) => {
        if (applySavedView(view)) {
            const cfg = getVolugraphConfig();
            if (cfg) cfg.volugraphHomeCameraView = view;
        }
    });
    events.on('volugraph.orbitAnchor.set', (anchor: VolugraphOrbitAnchor) => {
        const cfg = getVolugraphConfig();
        if (cfg && anchor && typeof anchor.x === 'number') {
            cfg.volugraphOrbitAnchor = anchor;
        }
    });
    events.on('volugraph.orbitAnchor.frame', () => {
        const cfg = getVolugraphConfig();
        if (cfg?.volugraphEditMode === true) return;
        frameCameraToOrbitAnchor(1.0);
    });
    events.on('camera.focalPointPicked', (payload: { position?: Vec3 } | undefined) => {
        const world = payload?.position;
        if (!world) return;
        events.fire('volugraph.orbitAnchor.set', { x: world.x, y: world.y, z: world.z });
    });
    events.on('volugraph.navRestoreHome', restoreHomeCameraView);

    // register events that need scene or other dependencies
    registerEditorEvents(events, editHistory, scene);
    registerSelectionEvents(events, scene);
    registerDocEvents(scene, events);
    registerRenderEvents(scene, events);
    initFileHandler(scene, events, editorUI.appContainer.dom);

    // load async models
    scene.start();

    const volugraphLoad = (window as unknown as {
        __VOLUGRAPH_SUPER_SPLAT_LOAD__?: { url?: string; filename?: string; contents?: Blob };
    }).__VOLUGRAPH_SUPER_SPLAT_LOAD__;

    if (volugraphLoad) {
        const filename = volugraphLoad.filename ?? 'capture.sog';
        try {
            if (volugraphLoad.contents) {
                await events.invoke('import', [{ filename, contents: volugraphLoad.contents }]);
            } else if (volugraphLoad.url) {
                await events.invoke('import', [{ filename, url: volugraphLoad.url }]);
            }
        } catch (e) {
            try {
                window.dispatchEvent(new CustomEvent('volugraph-splat-load-error', {
                    detail: { message: String((e as Error)?.message ?? e) }
                }));
            } catch {
                /* ignore */
            }
            // eslint-disable-next-line no-console
            console.warn('[Volugraph] SuperSplat import failed:', (e as Error)?.message ?? e);
            return;
        }
        events.fire('volugraph.splatLoaded');
        (window as unknown as { __VOLUGRAPH_SPLAT_LOAD_COMPLETE__?: boolean }).__VOLUGRAPH_SPLAT_LOAD_COMPLETE__ = true;
        try {
            window.dispatchEvent(new CustomEvent('volugraph-splat-loaded'));
            window.dispatchEvent(new CustomEvent('volugraph-load-stage', { detail: { stage: 'embed.firstFrame' } }));
        } catch {
            /* ignore */
        }
        // Frame the camera after layout settles (dimensions may be 0 on first frame)
        const frameCamera = () => {
            if (!isVolugraphSceneCameraLive()) return;
            const container = document.getElementById('canvas-container');
            if (container && container.clientWidth > 0 && container.clientHeight > 0) {
                const cfg = getVolugraphConfig();
                if (!applySavedView(cfg?.volugraphInitialCameraView)) {
                    if (!frameCameraToOrbitAnchor(0)) {
                        frameCameraToPipelineDefault(0);
                    }
                }
                applyInitialTransform(cfg?.volugraphInitialTransform);
                scheduleInitialTransformApply(cfg?.volugraphInitialTransform);
                if (cfg?.volugraphOrbitAnchor) {
                    events.fire('volugraph.orbitAnchor.import', cfg.volugraphOrbitAnchor);
                }
                snapshotHomeCameraView();
            } else {
                volugraphFrameCameraRaf = requestAnimationFrame(frameCamera);
            }
        };
        requestAnimationFrame(() => {
            requestAnimationFrame(() => frameCamera());
        });
    } else {
        // handle load params
        const loadList = url.searchParams.getAll('load');
        const filenameList = url.searchParams.getAll('filename');
        for (const [i, value] of loadList.entries()) {
            const decoded = decodeURIComponent(value);
            const filename = i < filenameList.length ?
                decodeURIComponent(filenameList[i]) :
                decoded.split('/').pop();

            await events.invoke('import', [{
                filename,
                url: decoded
            }]);
        }
    }


    // handle OS-based file association in PWA mode (setConsumer replaces prior handler — keep current `events`)
    if ('launchQueue' in window) {
        window.launchQueue.setConsumer(async (launchParams: LaunchParams) => {
            for (const file of launchParams.files) {
                await events.invoke('import', [{
                    filename: file.name,
                    contents: await file.getFile()
                }]);
            }
        });
    }
}

const main = async (): Promise<void> => {
    const w = window as unknown as {
        __VOLUGRAPH_MOUNT__?: () => Promise<void>;
        __VOLUGRAPH_DESTROY__?: () => Promise<void>;
        __VOLUGRAPH_SUPER_SPLAT_LOAD__?: unknown;
        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbed?: boolean };
    };
    w.__VOLUGRAPH_MOUNT__ = volugraphMount;
    w.__VOLUGRAPH_DESTROY__ = volugraphDestroy;
    const hasEmbedContract =
        !!w.__VOLUGRAPH_SUPER_SPLAT_LOAD__ ||
        w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphEmbed === true;
    if (hasEmbedContract) {
        await volugraphMount();
    }
};

export { main, volugraphMount, volugraphDestroy };
